// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Calendar, MapPin, Users, DollarSign, Trophy, FileText, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { parseLocalDate } from '@/utils/formatters';
import ScoreSheetGenerator from '@/components/tournament/ScoreSheetGenerator';
import CourtScheduleDrawer from '@/components/tournament/CourtScheduleDrawer';

interface Event {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  venue_name: string;
  status: string;
}

const StaffManageEvent: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [registrationCount, setRegistrationCount] = useState(0);

  const [scoreSheetsOpen, setScoreSheetsOpen] = useState(false);
  const [courtScheduleOpen, setCourtScheduleOpen] = useState(false);

  useEffect(() => {
    const checkPermissionAndLoadData = async () => {
      if (!user || !eventId) return;

      try {
        // Check if staff has manage_event permission
        const { data: assignments } = await supabase
          .from('event_staff_assignments')
          .select('permissions, event_staff(profile_id)')
          .eq('event_id', eventId)
          .eq('event_staff.profile_id', user.id)
          .single();

        if (!assignments) {
          toast.error('You do not have permission to manage this event');
          navigate('/staff/dashboard');
          return;
        }

        const permissions = assignments.permissions || [];
        if (!permissions.includes('all') && !permissions.includes('manage_event')) {
          toast.error('You do not have permission to manage this event');
          navigate('/staff/dashboard');
          return;
        }

        setHasPermission(true);

        // Load event details
        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select('id, name, start_date, end_date, venue_name, status')
          .eq('id', eventId)
          .single();

        if (eventError) throw eventError;
        setEvent(eventData);

        // Load registration count
        const { count } = await supabase
          .from('registrations')
          .select('*', { count: 'exact', head: true })
          .eq('event_id', eventId)
          .eq('status', 'confirmed');

        setRegistrationCount(count || 0);
      } catch (error) {
        console.error('Error loading event data:', error);
        toast.error('Failed to load event data');
      } finally {
        setLoading(false);
      }
    };

    checkPermissionAndLoadData();
  }, [user, eventId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  if (!hasPermission || !event) {
    return null;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/staff/dashboard')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold font-heading">{event.name}</h1>
        <p className="text-muted-foreground">Manage event settings and operations</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Event Status</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">{event.status}</div>
            <p className="text-xs text-muted-foreground">
              {parseLocalDate(event.start_date).toLocaleDateString()} - {parseLocalDate(event.end_date).toLocaleDateString()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Registrations</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{registrationCount}</div>
            <p className="text-xs text-muted-foreground">Confirmed registrations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Venue</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{event.venue_name || 'TBD'}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common event management tasks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => navigate(`/staff/registrations/${eventId}`)}
            >
              <Users className="mr-2 h-4 w-4" />
              View Registrations
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => navigate(`/staff/check-in?event=${eventId}`)}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Event Check-In
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => navigate(`/staff/reports/${eventId}`)}
            >
              <DollarSign className="mr-2 h-4 w-4" />
              View Reports
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => navigate(`/staff/events/${eventId}`)}
            >
              <Trophy className="mr-2 h-4 w-4" />
              Live Scoring
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => setScoreSheetsOpen(true)}
            >
              <FileText className="mr-2 h-4 w-4" />
              Score Sheets
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => setCourtScheduleOpen(true)}
            >
              <ClipboardList className="mr-2 h-4 w-4" />
              Court Schedule
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Event Information</CardTitle>
            <CardDescription>Basic event details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Event Name</p>
              <p className="text-base">{event.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Dates</p>
              <p className="text-base">
                {parseLocalDate(event.start_date).toLocaleDateString()} - {parseLocalDate(event.end_date).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Venue</p>
              <p className="text-base">{event.venue_name || 'Not specified'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <ScoreSheetGenerator eventId={eventId} open={scoreSheetsOpen} onClose={() => setScoreSheetsOpen(false)} />
      <CourtScheduleDrawer eventId={eventId} open={courtScheduleOpen} onClose={() => setCourtScheduleOpen(false)} />
    </div>
  );
};

export default StaffManageEvent;
